import { site } from './site'
import type { LegalDocument } from '../types/legal'

const operator = `${site.brandName} is operated by ${site.legalEntity}.`

export const privacyPolicy: LegalDocument = {
  title: 'Privacy Policy',
  intro: `${operator} This Privacy Policy explains what information we collect when you visit ${site.domain} or purchase a digital product from us, how that information is used, and how it is protected.`,
  sections: [
    {
      heading: '1. Information we may collect',
      paragraphs: [
        'We collect only the information needed to operate our business, deliver digital products and respond to customer enquiries.',
      ],
      bullets: [
        'Contact information such as your name and email address, provided when you contact us or complete a purchase.',
        'Order information such as the digital product purchased, the transaction reference and the date of purchase.',
        'Billing details required to process a payment. Full payment card numbers are processed by our third-party payment providers and are never stored on our systems.',
        'Technical information such as browser type, device type, approximate location and pages visited, collected automatically for security and analytics purposes.',
        'Any information you voluntarily include in a message sent to our support team.',
      ],
    },
    {
      heading: '2. How we use your information',
      bullets: [
        'To deliver digital products and provide access to purchased content.',
        'To process payments and prevent fraudulent transactions.',
        'To respond to customer support, billing and policy enquiries.',
        'To send transactional messages such as order confirmations and delivery instructions.',
        'To maintain, secure and improve our website and services.',
        'To comply with legal, tax and accounting obligations.',
      ],
      paragraphs: [
        'We do not sell your personal information, and we do not share it with third parties for their own marketing purposes.',
      ],
    },
    {
      heading: '3. Service providers',
      paragraphs: [
        'We work with a limited number of service providers who process data on our behalf, including payment processors, email and hosting providers, and website analytics services. These providers may only use the information for the purpose of supplying their service to us and are required to protect it.',
      ],
    },
    {
      heading: '4. How your information is protected',
      paragraphs: [
        'Our website is served over encrypted HTTPS connections. Access to customer information is restricted to the people who require it in order to operate the business or provide support. Payment information is handled directly by PCI-compliant payment processors.',
        'No method of transmission or storage is completely secure, but we take reasonable technical and organisational measures to protect the information we hold.',
      ],
    },
    {
      heading: '5. Cookies and analytics',
      paragraphs: [
        'We may use cookies and similar technologies to keep the website working correctly, remember basic preferences and understand how visitors use our pages. Analytics data is used in aggregate form and is not used to identify individual visitors.',
        'You can disable or delete cookies in your browser settings. Some parts of the website may not function correctly if cookies are disabled.',
      ],
    },
    {
      heading: '6. Data retention',
      paragraphs: [
        'We retain order and transaction records for as long as required for accounting, tax and legal purposes. Support correspondence is retained only as long as needed to resolve the enquiry and maintain a record of the request.',
      ],
    },
    {
      heading: '7. Your rights',
      paragraphs: [
        'Depending on where you live, you may have the right to request access to the personal information we hold about you, request that it be corrected or deleted, or object to certain types of processing. To make a request, contact us at the address below. We may need to verify your identity before acting on a request.',
      ],
    },
    {
      heading: "8. Children's privacy",
      paragraphs: [
        'Our website and products are intended for adults. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so that it can be removed.',
      ],
    },
    {
      heading: '9. Changes to this policy',
      paragraphs: [
        'We may update this Privacy Policy from time to time. Any updated version will be published on this page with a revised date.',
      ],
    },
    {
      heading: '10. Contact for privacy questions',
      paragraphs: [
        `For any question about this policy or about the information we hold, contact ${site.privacyEmail}.`,
      ],
    },
  ],
}

export const termsAndConditions: LegalDocument = {
  title: 'Terms & Conditions',
  intro: `These Terms & Conditions govern your use of ${site.domain} and any digital product purchased from ${site.brandName}. ${operator} By using this website you agree to these terms.`,
  sections: [
    {
      heading: '1. Use of the website',
      paragraphs: [
        'This website is provided for information about our business and for the purchase of digital products. You agree to use the website lawfully and not to attempt to interfere with its operation, security or availability.',
      ],
    },
    {
      heading: '2. Nature of the products',
      paragraphs: [
        'All products offered are digital products delivered electronically. These include downloadable guides and educational resources, printable templates and creative resources, craft and DIY learning materials, and downloadable puzzle and digital entertainment content.',
        'No physical goods are sold, packaged or shipped. Purchasing a digital product grants access to content only; it does not include any physical item, ongoing service or personalised consultation unless expressly stated at the point of purchase.',
      ],
    },
    {
      heading: '3. Intellectual property',
      paragraphs: [
        `All content on this website and within our digital products, including text, layouts, graphics, templates and brand marks, is owned by ${site.legalEntity} or its licensors and is protected by applicable copyright and trademark law.`,
        'Unless otherwise stated at the point of purchase, a purchase grants you a personal, non-exclusive, non-transferable licence to use the content for your own personal or internal use. You may not resell, redistribute, sublicense, publish or share purchased content without our written permission.',
      ],
    },
    {
      heading: '4. User responsibilities',
      bullets: [
        'Provide accurate contact and billing information when making a purchase.',
        'Ensure the email address supplied is correct and able to receive delivery messages.',
        'Use purchased content only in accordance with the licence granted.',
        'Do not attempt to gain unauthorised access to our systems, accounts or content.',
        'Do not use our products or website for any unlawful purpose.',
      ],
    },
    {
      heading: '5. Pricing and payment',
      paragraphs: [
        'Prices are displayed at the point of purchase and may change at any time before an order is placed. Payments are processed by third-party payment providers through secure checkout. We do not store full payment card details.',
      ],
    },
    {
      heading: '6. Availability and accuracy',
      paragraphs: [
        'We take care to describe our products accurately, but we do not warrant that content will meet every individual requirement, or that the website will be uninterrupted or error free. Educational and informational content is provided for general purposes only and does not constitute professional advice.',
      ],
    },
    {
      heading: '7. Limitation of liability',
      paragraphs: [
        'To the maximum extent permitted by law, our total liability arising from or connected to a purchase is limited to the amount actually paid for the product in question. We are not liable for indirect, incidental or consequential losses, including loss of profit or data, arising from use of the website or of our digital products.',
        'Nothing in these terms limits any liability that cannot lawfully be limited.',
      ],
    },
    {
      heading: '8. Termination',
      paragraphs: [
        'We may suspend or withdraw access to the website or to purchased content where these terms are breached, including in cases of unauthorised redistribution or fraudulent payment activity.',
      ],
    },
    {
      heading: '9. Governing business information',
      paragraphs: [
        `${site.brandName} is a brand operated by ${site.legalEntity}, a ${site.jurisdiction} limited liability company conducting business online. These terms are governed by the laws applicable in that jurisdiction, without regard to conflict of law principles.`,
      ],
    },
    {
      heading: '10. Changes and contact',
      paragraphs: [
        `We may revise these terms from time to time. The version published on this page applies to your use of the website. Questions about these terms can be sent to ${site.supportEmail}.`,
      ],
    },
  ],
}

export const refundPolicy: LegalDocument = {
  title: 'Refund Policy',
  intro: `${operator} We sell digital products that are delivered electronically. This policy explains when a refund may be available and how to request support.`,
  sections: [
    {
      heading: '1. Digital products',
      paragraphs: [
        'All products sold are digital and are delivered electronically immediately or shortly after payment. Because access to digital content cannot be returned once it has been provided, refund eligibility is limited after delivery or access has been granted.',
      ],
    },
    {
      heading: '2. When a refund is normally provided',
      paragraphs: [
        'We want every customer to receive what they paid for. A refund is normally provided in the following situations:',
      ],
      bullets: [
        'A duplicate purchase was made in error and the duplicate content was not accessed.',
        'Access or delivery was not provided and the issue could not be resolved by our support team.',
        'The product delivered is materially different from the product described at the point of purchase.',
        'A technical fault prevents the content from being opened or used and cannot be corrected.',
        'A payment was made without authorisation, once verified.',
      ],
    },
    {
      heading: '3. When a refund may not be available',
      bullets: [
        'The content has been downloaded, opened or accessed and matches its description.',
        'The request is based on a change of mind after delivery has been completed.',
        'The customer does not have compatible software or hardware described in the product information.',
        'The request is made a significant period after the purchase date, outside the window described below.',
      ],
    },
    {
      heading: '4. How to request support or a refund',
      paragraphs: [
        `Contact ${site.supportEmail} within 14 days of purchase and include your order reference, the email address used at checkout and a short description of the problem. Our team will review the request and, where possible, resolve the issue by restoring access or resending the content.`,
        'Approved refunds are returned to the original payment method. Processing times depend on the payment provider and the customer\u2019s bank, and typically take 5\u201310 business days.',
      ],
    },
    {
      heading: '5. Fair use',
      paragraphs: [
        'This policy is applied in good faith and in a consistent way for all customers. Statutory consumer rights that apply in your country are not affected by this policy.',
      ],
    },
  ],
}

export const digitalDeliveryPolicy: LegalDocument = {
  title: 'Digital Delivery Policy',
  intro: `${operator} This policy explains how products are delivered. All products are digital and are supplied electronically. No physical items are shipped.`,
  sections: [
    {
      heading: '1. Digital products only',
      paragraphs: [
        'Every product offered is a digital product. We do not sell, package or ship physical goods, and no shipping address, courier service or delivery charge is involved in any order.',
      ],
    },
    {
      heading: '2. How delivery works',
      paragraphs: [
        'Once payment has been confirmed, access to the purchased content is provided electronically. In most cases this happens immediately after checkout. Where a payment requires additional verification by the payment provider, access is provided as soon as the payment is confirmed.',
      ],
    },
    {
      heading: '3. Access methods',
      paragraphs: [
        'The access method depends on the type of product purchased. Common methods include:',
      ],
      bullets: [
        'A secure download link shown on the order confirmation page.',
        'A confirmation email containing download or access instructions.',
        'Access to content hosted online through a link provided after purchase.',
      ],
      // eslint-disable-next-line
    },
    {
      heading: '4. Delivery timing',
      paragraphs: [
        'Digital delivery is normally immediate. If a delay occurs due to a payment review or a temporary technical issue, delivery is completed as soon as the issue is resolved, and our support team can confirm the status of an order at any time.',
      ],
    },
    {
      heading: '5. If you do not receive access',
      paragraphs: [
        `If access has not arrived, please check the spam or promotions folder of the email address used at checkout. If the content still cannot be found, contact ${site.supportEmail} with your order reference and the email address used for the purchase. Support enquiries are typically answered within 1\u20132 business days, and access will be restored or resent once the order is verified.`,
      ],
    },
    {
      heading: '6. Technical requirements',
      paragraphs: [
        'Most products are supplied in widely supported formats such as PDF. Any specific requirement is stated in the product information before purchase. A stable internet connection is required to download or access content.',
      ],
    },
  ],
}
